Test data discipline keeps workflows reliable and concurrent-safe.

PREFER SEED WORKFLOWS FOR DATA

  Seed workflows are the best way to provision test data. They run before
  your test workflow, create the data it needs, and export variables that
  downstream workflows can reference as $var.{dataKey}.

  Benefits of seeds over inline data creation:
  - Reusable across multiple workflows
  - Faster — can use API sequences instead of browser interactions
  - Cleaner separation of setup and test logic
  - Paired teardown workflows handle cleanup automatically

  See the Seed Workflows section for how to build and use them.

SANDBOX BASE-SEED DATA ($var.SEED.*)

  On a sandbox environment, core baseline data (the application/property, a
  credential, sometimes a starter workflow) is provisioned automatically by a
  base seed and exposed as $var.SEED.{key} — e.g. $var.SEED.property_id.

  Prefer these over hardcoded names when you need a core entity that already
  exists on every tenant:
    Good:  "Open the property $var.SEED.property_id"

  Base seeds are core-only. Anything test-specific (an issue, a notification,
  run history) is NOT in the base seed — create it yourself (see below) or via
  a per-test seed/custom action.

THE DATA OWNERSHIP PRINCIPLE

  Own your data:
  - Data you create → clean it up when the workflow finishes
  - Settings you modify → revert them when the workflow finishes
  - Data that was already there → never delete it

  It's fine to depend on pre-existing data (existing integrations, org
  settings, reference data). Only create what doesn't already exist or
  what the test specifically needs to own.

CREATING TEST DATA

  If the test operates on a record (edit, delete, archive), create that
  record as an early step — unless it already exists and the test is meant
  to operate on it.

  Never reference "any" resource:
    Bad:   "Pick a project from the list" (which one? may not exist)
    Good:  "Create a project named 'QA Test — $RANDOM_STRING(6)'"
    Good:  "Use the seed output $var.project_id"

  Always use macros in names to ensure uniqueness across parallel runs:
    Bad:   "Test Item" (generic, will collide with other runs)
    Bad:   "QA Test Project" (still collides if two runs overlap)
    Good:  "QA Test — $RANDOM_STRING(6)" (unique every run)
    Good:  "QA Test $FIRST_NAME() $LAST_NAME()" (unique and readable)

  After creating data, verify it exists before continuing.

NAMING CONVENTION

  All test data should follow this pattern:

    Name/Title:    "QA Test $FIRST_NAME() $LAST_NAME()"
    Email:         "$EMAIL()"
    Phone:         "$PHONE()"
    Project/Item:  "QA Test — $RANDOM_STRING(6)"
    Unique ID:     "QA$TIMESTAMP()"

  The "QA Test" prefix makes test data instantly recognizable. The macro
  suffix ensures every run creates unique data that won't collide with
  other runs or leftover data from previous executions.

CLEANING UP (STRONGLY RECOMMENDED)

  Workflows that create data should include cleanup steps at the end.
  Workflows that modify settings should revert them.

  This isn't strictly required, but it's strongly recommended:
  - Cleanup prevents data accumulation over time
  - It keeps the test environment clean for other workflows
  - The "QA Test" naming convention makes orphaned data identifiable
    even when cleanup is skipped or fails

  Don't over-engineer cleanup — a simple "navigate to the item and
  delete it" is sufficient. For seed workflows, use a paired teardown
  workflow instead.

WHAT NOT TO TOUCH

  - Don't create users, roles, or org-level config unless that's the goal
  - Don't modify shared resources (billing, integrations, API keys) as a side effect
  - Don't delete or modify data not owned by this workflow

AVOIDING COLLISIONS WITH MACROS

  The best way to avoid data collisions is to use macros for every
  name, email, and identifier. When every run generates unique values,
  there's no risk of conflicting with parallel runs or leftover data:

    $EMAIL()            Unique email every run
    $RANDOM_STRING(6)   Short unique suffix for names
    $UUID()             Globally unique identifier
    $TIMESTAMP()        Unique-per-millisecond numeric ID

  Combined with the "QA Test" prefix, this means you never need to
  worry about whether data from a previous run still exists. Each run
  creates its own isolated dataset.

READ-ONLY FLOWS

  Workflows that only read data (verify dashboard loads, check reports)
  don't need creation or cleanup steps. Not every workflow needs to
  create data — only create what the test specifically requires.